import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop } from '@stencil/core';
import { isKeyboardClickOnFocusedElement } from '../../utils/is-keyboard-click-on-focused-element';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { renderInputNativeInputChild } from '../../shared/form.util';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { getRandomId } from '../../utils/get-random-id';

/**
 * A Toggle is a user interface element that allows users to switch between two options. It has an active and a default
 * state. The selected option is displayed as active while the remaining option is shown in its default state. Toggles
 * can be used to group content within different modules. Upon clicking on an inactive option the content related to
 * this option is shown and the remaining option is deactivated.
 *
 * @slot - The label which should be displayed right next to the toggle
 * @slot message - `wb-control-error` or `wb-control-hint` to further describe the current state
 */
@Component({
  tag: 'wb-toggle',
  styleUrl: './toggle.scss',
  shadow: true,
})
export class ToggleComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** @internal reflects global rtl */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** Disables the checkbox */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** Marks control as invalid. Automatically set by slotted `wb-control-error` */
  @Prop({ reflect: true, mutable: true })
  public error: boolean = false;

  /** Draws the toggle in a checked state */
  @Prop({ mutable: true, reflect: true })
  public checked: boolean = false;

  /** Determines whether the input is required or not */
  @Prop()
  public required?: boolean = false;

  /** The checkbox's value attribute */
  @Prop()
  public value: string = 'on';

  /** The checkbox's name attribute */
  @Prop()
  public name!: string;

  /** Emitted when the checked state changes */
  @Event()
  public wbchange: EventEmitter<{ name: string; value: string; checked: boolean }>;

  /** Emitted when the checked state changes */
  @Event({ composed: false })
  public change: EventEmitter<void>;

  /** Emitted when the checkbox loses focus */
  @Event()
  public wbblur: EventEmitter<void>;

  /** Emitted when the checkbox receives focus */
  @Event()
  public wbfocus: EventEmitter<void>;

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  @Listen('keydown')
  public handleKeyDown(ev: KeyboardEvent): void {
    if (isKeyboardClickOnFocusedElement(this.el, ev)) {
      ev.preventDefault();
      this.inputEl.click();
    }
  }

  private bindRef = (el) => {
    this.inputEl = el;
  };

  /** returns a reference to the native input element */
  @Method()
  public async getInputReference(): Promise<HTMLInputElement> {
    return this.inputEl;
  }

  private handleChange = (evt: InputEvent): void => {
    this.checked = (evt.target as HTMLInputElement).checked;
    this.wbchange.emit(this.getValueChangeEventDetail());
    this.change.emit();
  };

  private handleBlur = () => this.wbblur.emit();

  private handleFocus = () => this.wbfocus.emit();

  private getValueChangeEventDetail(): { name: string; value: string; checked: boolean } {
    return { name: this.name, value: this.value, checked: this.checked };
  }

  private handleMessageSlotChange = () => {
    const messageEl = this.el.querySelector<HTMLWbControlErrorElement | HTMLWbControlHintElement>(`${this.TagNames.WbControlError},${this.TagNames.WbControlHint}`);

    if (messageEl) {
      messageEl.theme = this.theme;
      messageEl.id = messageEl.id || this.randomMessageId;
      this.inputEl.setAttribute('aria-describedby', messageEl.id);
    } else {
      this.inputEl.removeAttribute('aria-describedby');
    }

    this.error = Boolean(this.el.querySelector(this.TagNames.WbControlError));
  };

  private TagNames = transformTagNames(this, 'wb-toggle', ['wb-control-error', 'wb-control-hint', 'wb-text']);

  private inputEl!: HTMLInputElement;
  private randomInputId: string;
  private randomMessageId: string;

  public connectedCallback(): void {
    this.randomInputId = `input-${getRandomId()}`;
  }

  public render(): JSX.Element {
    const { WbText } = this.TagNames;

    renderInputNativeInputChild(this.el, {
      type: 'checkbox',
      name: this.name,
      value: this.value,
      checked: this.checked,
      disabled: this.disabled,
    });

    return (
      // should only be tabbable if not disabled
      <Host class="wb-toggle" data-on={getDataOnFromTheme(this.el, this.theme)} tabindex={!this.disabled ? '0' : ''}>
        <div class="container">
          <input
            //
            ref={this.bindRef}
            type="checkbox"
            data-testid="toggle-checkbox"
            id={this.randomInputId}
            checked={this.checked}
            disabled={this.disabled}
            value={this.value}
            name={this.name}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            required={this.required}
          />
          <label htmlFor={this.randomInputId}>
            <WbText size="l" static>
              <slot />
            </WbText>
            <slot name="message" onSlotchange={this.handleMessageSlotChange} />
          </label>
        </div>
      </Host>
    );
  }
}
